import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Heartbeat, Plugs, LinkBreak } from "@phosphor-icons/react";
import HealthAnalytics from "@/components/health/HealthAnalytics";
import GpsWeekCard from "@/components/health/GpsWeekCard";
import WorkoutLogCard from "@/components/health/WorkoutLogCard";
import DayStoryCard from "@/components/health/DayStoryCard";

export default function Health() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    const whoopParam = params.get("whoop");
    if (whoopParam === "connected") { toast.success("Whoop успешно подключён!"); setParams({}); }
    if (whoopParam === "error") { toast.error("Ошибка подключения Whoop"); setParams({}); }
    api.get("/whoop/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/whoop/connect");
      window.location.href = data.auth_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  const disconnect = async () => {
    await api.post("/whoop/disconnect");
    setStatus((s) => ({ ...s, connected: false }));
    toast.success("Whoop отключён");
  };

  return (
    <div className="fade-in" data-testid="health-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Фитнес-трекер</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">Здоровье</h1>
        </div>
        {status?.connected && (
          <Button variant="outline" onClick={disconnect} data-testid="whoop-disconnect-button">
            <LinkBreak size={16} className="mr-1" /> Отключить Whoop
          </Button>
        )}
      </div>

      {status && !status.configured && (
        <div className="rounded-lg border border-[#ffea00]/30 bg-[#ffea00]/5 p-8" data-testid="whoop-not-configured">
          <div className="flex items-start gap-4">
            <Plugs size={28} className="text-[#ffea00] shrink-0" />
            <div>
              <h2 className="font-heading font-semibold text-lg mb-2">Whoop API не настроен</h2>
              <p className="text-sm text-muted-foreground max-w-2xl leading-relaxed">
                Для подключения Whoop нужны учётные данные разработчика. Получите их на{" "}
                <a href="https://developer.whoop.com" target="_blank" rel="noreferrer" className="text-primary hover:underline">developer.whoop.com</a>:
                создайте команду и приложение, укажите Redirect URI, скопируйте Client ID и Client Secret —
                и передайте их для завершения настройки интеграции.
              </p>
            </div>
          </div>
        </div>
      )}

      {status?.configured && !status.connected && (
        <div className="space-y-5">
          <div className="glass-card p-12 text-center" data-testid="whoop-connect-card">
            <Heartbeat size={40} weight="duotone" className="text-primary mx-auto mb-4" />
            <h2 className="font-heading font-semibold text-lg mb-2">Подключите Whoop</h2>
            <p className="text-sm text-muted-foreground mb-6">Восстановление, сон и нагрузка — прямо в вашем дашборде</p>
            <Button onClick={connect} disabled={busy} data-testid="whoop-connect-button" className="active:scale-95 transition-transform">
              {busy ? "Переход…" : "Подключить Whoop"}
            </Button>
          </div>
          <DayStoryCard />
          <WorkoutLogCard />
          <GpsWeekCard />
        </div>
      )}

      {status?.connected && (
        <div className="space-y-5">
          <GpsWeekCard />
          <HealthAnalytics />
        </div>
      )}
    </div>
  );
}
